/*
 * Mock implementation of the Wecoma SDK - for DEV and QA
 * Call initWecomaMock() from an app entry point; it does nothing outside DEV / QA.
 */

const STORAGE_KEY = "wecoma-mock-consents";
const PURPOSE_CODES = ["analytics", "marketing", "thirdpartycontent"] as const;

type PurposeCode = (typeof PURPOSE_CODES)[number];
type MockConsentState = Record<PurposeCode, boolean> & { hasStatus: boolean };

function defaultState(): MockConsentState {
    return { analytics: false, marketing: false, thirdpartycontent: false, hasStatus: false };
}

function loadState(): MockConsentState {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return defaultState();
        }

        const parsed = JSON.parse(raw) as Partial<MockConsentState>;
        return {
            analytics: !!parsed.analytics,
            marketing: !!parsed.marketing,
            thirdpartycontent: !!parsed.thirdpartycontent,
            hasStatus: !!parsed.hasStatus,
        };
    } catch {
        return defaultState();
    }
}

function saveState(state: MockConsentState): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
        // storage unavailable - ignore
    }
}

function setPurpose(code: PurposeCode, granted: boolean): void {
    const state = loadState();
    state[code] = granted;
    state.hasStatus = true;
    saveState(state);
}

interface MockPurpose {
    code: PurposeCode;
    consent: () => Promise<void>;
    revoke: () => Promise<void>;
}

function makePurpose(code: PurposeCode): MockPurpose {
    return {
        code,
        consent: () => {
            setPurpose(code, true);
            return Promise.resolve();
        },
        revoke: () => {
            setPurpose(code, false);
            return Promise.resolve();
        },
    };
}

class MockWecoma {
    readonly clientAuthKey = "mock-client-auth-key";

    isConsented(code: PurposeCode): Promise<boolean> {
        return Promise.resolve(loadState()[code]);
    }

    registerUser(): Promise<{ registerManagedAuthKey: () => Promise<void> }> {
        return Promise.resolve({ registerManagedAuthKey: () => Promise.resolve() });
    }

    userHasLocalManagedAuthKey(): Promise<boolean> {
        return Promise.resolve(false);
    }

    allPurposesConsented(): Promise<boolean> {
        const state = loadState();
        return Promise.resolve(PURPOSE_CODES.every((code) => state[code]));
    }

    allPurposesRevoked(): Promise<boolean> {
        const state = loadState();
        return Promise.resolve(PURPOSE_CODES.every((code) => !state[code]));
    }

    anyPurposeHasStatus(): Promise<boolean> {
        return Promise.resolve(loadState().hasStatus);
    }

    retrievePurposes(): Promise<MockPurpose[]> {
        return Promise.resolve(PURPOSE_CODES.map(makePurpose));
    }

    retrievePurpose(code: PurposeCode): Promise<MockPurpose> {
        return Promise.resolve(makePurpose(code));
    }
}

/**
 * Installs the mock Wecoma SDK on `window`
 */
export function initWecomaMock(): void {
    if (environment.env !== "Development" && environment.env !== "QA") {
        return;
    }

    if (!window._gdpr) {
        window._gdpr = {
            wecoma: null,
            purposes: undefined,
            onPurposesLoaded: [],
        };
    }

    const queuedOnready: Array<() => void> = Array.isArray(window.Wecoma?.onready) ? window.Wecoma.onready.slice() : [];

    window.Wecoma = MockWecoma;

    window.Wecoma.onready = { push: (callback: () => void) => callback() };

    window._gdpr.wecoma = new MockWecoma();

    queuedOnready.forEach((callback) => callback());
}
